/**
 * カラーグレーディング (プリセット) の正本。
 *
 * 動画エディタのプロジェクト全体に 1 つ掛ける色味の設定で、ユーザーは「なし / シネマ風 / スマホ風 /
 * テレビCM風 / フィルム風」から選び、スイッチ (enabled) と強度 (intensity 0〜1) だけを操作する。
 * 細かいパラメータは触らせない。
 *
 * 描画はプレビュー (Player) と MP4 レンダラー (video-editor リポジトリ) の双方が Chromium なので、
 * CSS filter (brightness / contrast / saturate / sepia / hue-rotate) + 色かぶりのオーバーレイ
 * (mix-blend-mode: soft-light) + ビネット (radial-gradient) で同じ見た目になる。
 * テロップはグレーディングの対象外 (映像レイヤーだけに掛ける)。
 *
 * 【両リポジトリ同期】video-editor リポジトリの src/schema/lookCatalog.ts はこのファイルの写し
 * (import パス以外は同一)。プリセットの値や変換順を変えたら必ず両方を更新する。
 *
 * 書き出し (Premiere / Final Cut) には LUT (.cube) として同梱する (export/lookLut.ts)。LUT は
 * 空間的な効果 (ビネット) を表現できないので、LUT に入るのは filter + 色かぶりまで。
 * そのため LUT 生成側は、ここに書いた変換を「同じ順番で」数式として再現する必要がある:
 *   1. brightness (RGB を b 倍)
 *   2. contrast   (x → c·x + (0.5 − 0.5·c))
 *   3. saturate   (Filter Effects 仕様の行列)
 *   4. sepia      (Filter Effects 仕様の行列を amount で補間)
 *   5. hue-rotate (Filter Effects 仕様の行列)
 *   6. 色かぶり    (soft-light ブレンド。W3C Compositing 仕様。opacity で合成)
 *   7. ビネット    (LUT 対象外)
 */

export const LOOK_CATALOG_VERSION = 1;

export type LookPreset = 'cinema' | 'smartphone' | 'tvcm' | 'film';

export const LOOK_PRESETS: readonly LookPreset[] = ['cinema', 'smartphone', 'tvcm', 'film'];

export function isLookPreset(value: unknown): value is LookPreset {
  return typeof value === 'string' && (LOOK_PRESETS as readonly string[]).includes(value);
}

/** UI に出す表示名と一言説明 (AI 編集のプロンプトにも同じ文言を渡す)。 */
export const LOOK_PRESET_LABELS: Record<LookPreset, { label: string; description: string }> = {
  cinema: { label: 'シネマ風', description: 'コントラスト高め・彩度控えめ・寒色寄りの影と周辺減光' },
  smartphone: { label: 'スマホ風', description: '明るく高彩度でくっきり。SNS 動画の色味' },
  tvcm: { label: 'テレビCM風', description: '明るく清潔感のあるハイキー。白が抜けた広告の色味' },
  film: { label: 'フィルム風', description: '彩度を落として暖色に寄せた退色調。周辺減光あり' },
};

/**
 * カラーグレーディング 1 つぶんのパラメータ。強度 1 のときの値で、identity (何もしない) との間を intensity で
 * 線形補間する (resolveLookParams)。
 */
export interface LookParams {
  /** CSS brightness(). 1 = そのまま */
  brightness: number;
  /** CSS contrast(). 1 = そのまま */
  contrast: number;
  /** CSS saturate(). 1 = そのまま */
  saturate: number;
  /** CSS sepia() の量 0〜1。0 = なし */
  sepia: number;
  /** CSS hue-rotate() の角度 (deg)。0 = なし */
  hueRotate: number;
  /** 色かぶり (soft-light ブレンドで重ねる単色)。opacity 0 = なし */
  tint: { color: string; opacity: number };
  /** ビネット (周辺減光) の強さ 0〜1。0 = なし */
  vignette: number;
}

export const IDENTITY_LOOK_PARAMS: LookParams = {
  brightness: 1,
  contrast: 1,
  saturate: 1,
  sepia: 0,
  hueRotate: 0,
  tint: { color: '#000000', opacity: 0 },
  vignette: 0,
};

/** プリセットごとの強度 1 の値。 */
export const LOOK_PRESET_PARAMS: Record<LookPreset, LookParams> = {
  cinema: {
    brightness: 0.97,
    contrast: 1.14,
    saturate: 0.88,
    sepia: 0.08,
    hueRotate: 0,
    tint: { color: '#1a4d6e', opacity: 0.35 },
    vignette: 0.45,
  },
  smartphone: {
    brightness: 1.05,
    contrast: 1.08,
    saturate: 1.32,
    sepia: 0,
    hueRotate: 0,
    tint: { color: '#4da3ff', opacity: 0.12 },
    vignette: 0,
  },
  tvcm: {
    brightness: 1.06,
    contrast: 1.03,
    saturate: 1.12,
    sepia: 0,
    hueRotate: 0,
    tint: { color: '#ffffff', opacity: 0.2 },
    vignette: 0,
  },
  film: {
    brightness: 0.98,
    contrast: 0.95,
    saturate: 0.72,
    sepia: 0.25,
    hueRotate: 0,
    tint: { color: '#c8a878', opacity: 0.3 },
    vignette: 0.5,
  },
};

/** プロジェクトに保存するカラーグレーディング設定 (schema/project.ts の LookSchema と同じ形)。 */
export interface LookSetting {
  preset: LookPreset;
  /** 0〜1。省略時 1 */
  intensity?: number;
  /** false = 設定は残すが掛けない (スイッチ OFF)。省略時 true */
  enabled?: boolean;
}

export const DEFAULT_LOOK_INTENSITY = 1;

export function clampLookIntensity(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_LOOK_INTENSITY;
  return Math.min(1, Math.max(0, value));
}

/** カラーグレーディング設定が「実際に掛かる」状態か (未設定 / OFF / 強度 0 は false)。 */
export function isLookActive(look: LookSetting | null | undefined): look is LookSetting {
  if (!look || !isLookPreset(look.preset)) return false;
  if (look.enabled === false) return false;
  return clampLookIntensity(look.intensity ?? DEFAULT_LOOK_INTENSITY) > 0;
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

/**
 * カラーグレーディング設定を、強度を織り込んだ実効パラメータに解決する。
 * 掛からない状態 (isLookActive が false) では identity を返す。
 */
export function resolveLookParams(look: LookSetting | null | undefined): LookParams {
  if (!isLookActive(look)) return IDENTITY_LOOK_PARAMS;
  const t = clampLookIntensity(look.intensity ?? DEFAULT_LOOK_INTENSITY);
  const p = LOOK_PRESET_PARAMS[look.preset];
  const id = IDENTITY_LOOK_PARAMS;
  return {
    brightness: lerp(id.brightness, p.brightness, t),
    contrast: lerp(id.contrast, p.contrast, t),
    saturate: lerp(id.saturate, p.saturate, t),
    sepia: lerp(id.sepia, p.sepia, t),
    hueRotate: lerp(id.hueRotate, p.hueRotate, t),
    tint: { color: p.tint.color, opacity: lerp(0, p.tint.opacity, t) },
    vignette: lerp(id.vignette, p.vignette, t),
  };
}

function fmt(value: number): string {
  // CSS に渡す小数は 4 桁で十分 (テストの安定性のため固定桁にする)
  return String(Math.round(value * 10000) / 10000);
}

/**
 * 映像レイヤーに掛ける CSS filter 文字列。identity のときは undefined (filter を付けない)。
 * 関数の並び順は LUT 生成 (export/lookLut.ts) と一致させること。
 */
export function lookCssFilter(params: LookParams): string | undefined {
  const parts: string[] = [];
  if (params.brightness !== 1) parts.push(`brightness(${fmt(params.brightness)})`);
  if (params.contrast !== 1) parts.push(`contrast(${fmt(params.contrast)})`);
  if (params.saturate !== 1) parts.push(`saturate(${fmt(params.saturate)})`);
  if (params.sepia !== 0) parts.push(`sepia(${fmt(params.sepia)})`);
  if (params.hueRotate !== 0) parts.push(`hue-rotate(${fmt(params.hueRotate)}deg)`);
  return parts.length > 0 ? parts.join(' ') : undefined;
}

/**
 * 色かぶりオーバーレイのスタイル (映像の上・テロップの下に AbsoluteFill で置く)。
 * opacity 0 のときは null (描画しない)。
 */
export function lookTintStyle(params: LookParams): { backgroundColor: string; opacity: number; mixBlendMode: 'soft-light' } | null {
  if (params.tint.opacity <= 0) return null;
  return { backgroundColor: params.tint.color, opacity: params.tint.opacity, mixBlendMode: 'soft-light' };
}

/** ビネットの最大濃さ (vignette=1 のときの外周の黒の alpha)。 */
export const LOOK_VIGNETTE_MAX_ALPHA = 0.7;

/**
 * ビネット (周辺減光) オーバーレイのスタイル。vignette 0 のときは null。
 * 中心 55% までは透明、外周に向けて黒を alpha (LOOK_VIGNETTE_MAX_ALPHA × vignette) まで乗せる。
 */
export function lookVignetteStyle(params: LookParams): { background: string } | null {
  if (params.vignette <= 0) return null;
  const alpha = fmt(LOOK_VIGNETTE_MAX_ALPHA * params.vignette);
  return { background: `radial-gradient(ellipse at center, rgba(0,0,0,0) 55%, rgba(0,0,0,${alpha}) 100%)` };
}

// ---------------------------------------------------------------------------
// シーン単位の色補正 (§4.1-K)
//
// プロジェクト全体の look (LookSetting) に対して、シーン (TimelineItem.look) が
// 「このシーンだけ別のプリセットにする」「このシーンだけ補正を外す」を表す。
// 解決順は resolveItemLook (3 実装で同値: ここ / video-editor の写し /
// backend/services/video_edit/look.py)。
// ---------------------------------------------------------------------------

/**
 * シーンに持たせる色補正の指定。
 * - `{ disabled: true }`: このシーンだけカラーグレーディングを掛けない (全体の look を外す)
 * - `{ preset, intensity? }`: このシーンだけ別のプリセット (強度は 0〜1・省略時 1)
 * - 省略 (undefined): プロジェクトの look を継承する
 */
export interface ItemLookSetting {
  preset?: LookPreset;
  intensity?: number;
  disabled?: boolean;
}

/** 強度の丸め (0.01 刻みの half-up)。3 実装で同じ値にするため保存前に必ず通す。 */
function roundIntensity(value: number): number {
  return Math.floor(value * 100 + 0.5) / 100;
}

/**
 * 外部入力 (AI の提案・UI・古い保存データ) のシーン look を保存形へ正規化する。
 * `disabled: true` が最優先 (他のキーは無視)。プリセットが不正なら undefined
 * (= 継承に戻す) を返す。
 */
export function normalizeItemLook(value: unknown): ItemLookSetting | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  if (raw.disabled === true) return { disabled: true };
  if (!isLookPreset(raw.preset)) return undefined;
  const intensity = roundIntensity(
    clampLookIntensity(raw.intensity === undefined ? DEFAULT_LOOK_INTENSITY : raw.intensity),
  );
  return { preset: raw.preset, intensity };
}

/**
 * そのシーンに実際に掛かるカラーグレーディングを解決する (§4.1-K)。
 *
 * 1. シーンの look が `disabled: true` → `null` (補正なし)
 * 2. シーンの look にプリセットがある → そのプリセット (強度込み・enabled: true)
 * 3. それ以外 (省略・壊れた値) → プロジェクトの look をそのまま (未設定なら null)
 *
 * 戻り値が `null` でなくても、`isLookActive` が false (OFF・強度 0) なら描画では掛からない。
 */
export function resolveItemLook(
  projectLook: LookSetting | null | undefined,
  itemLook: ItemLookSetting | null | undefined,
): LookSetting | null {
  const item = normalizeItemLook(itemLook);
  if (item?.disabled) return null;
  if (item?.preset) {
    return {
      preset: item.preset,
      intensity: item.intensity ?? DEFAULT_LOOK_INTENSITY,
      enabled: true,
    };
  }
  return projectLook ?? null;
}

/** シーンの look が「全体と違う指定」を持っているか (UI のバッジ・lint・書き出しの判定)。 */
export function hasItemLookOverride(itemLook: ItemLookSetting | null | undefined): boolean {
  return normalizeItemLook(itemLook) !== undefined;
}

/** UI / 要約 / README に出す 1 行 (backend の describe_item_look と同じ文言)。 */
export function itemLookLabel(itemLook: ItemLookSetting | null | undefined): string {
  const item = normalizeItemLook(itemLook);
  if (!item) return '全体に従う';
  if (item.disabled) return 'このシーンだけ外す';
  const label = LOOK_PRESET_LABELS[item.preset as LookPreset]?.label ?? item.preset;
  const percent = Math.round((item.intensity ?? DEFAULT_LOOK_INTENSITY) * 100);
  return `${label} ${percent}%`;
}
